import { defineStore } from "pinia";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const memberContactPreferencesCollection = "memberContactPreferenceForms";
const impersonationStorageKey = "gcdp.impersonated_uid";

export const useCurrentUserStore = defineStore("currentUser", {
  state: () => ({
    roles: [],
    actual_roles: [],
    loading: true,
    loadingContact: false,
    loadingMemberships: false,
    loadingMoreMemberships: false,
    loadingContributions: false,
    initialized: false,
    lastMembership: null,
    contactId: null,
    availableContacts: [],
    selectedContactId: null,
    memberships: [],
    allMemberships: [],
    membershipPageSize: 5,
    membershipTotalCount: 0,
    contributions: [],
    contact: null,
    contactRef: null,
    contactLookupAttempted: false,
    impersonated_uid: null,
    storageSyncInitialized: false,
    storageSyncHandler: null,
    user: null,
    authUnsubscribe: null,
    mobileNumber: "",
  }),
  computed: {
    auth() {
      return getAuth();
    },
  },
  getters: {
    hasMultipleContacts: state => state.availableContacts.length > 1,
    effectiveUid: state => state.impersonated_uid || state.user?.uid || null,
    isImpersonating: state => Boolean(state.impersonated_uid),
    isActualAdmin: state => state.actual_roles.includes("admin"),
  },
  actions: {
    resetUserScopedState() {
      this.roles = [];
      this.lastMembership = null;
      this.contactId = null;
      this.availableContacts = [];
      this.selectedContactId = null;
      this.memberships = [];
      this.allMemberships = [];
      this.membershipTotalCount = 0;
      this.contributions = [];
      this.contact = null;
      this.contactRef = null;
      this.contactLookupAttempted = false;
      this.loadingContact = false;
      this.loadingMemberships = false;
      this.loadingContributions = false;
      this.loadingMoreMemberships = false;
      this.mobileNumber = "";
    },

    async refreshCurrentUserData() {
      if (!this.user) {
        this.resetUserScopedState();
        this.loading = false;
        return;
      }

      this.resetUserScopedState();
      this.loading = true;
      this.loadingContact = true;

      try {
        await this.getActualUserRoles(true);
        await this.loadPreferredContactSelection();
        await this.getContact(true);
        await this.getCurrentUserRoles(true);
      } finally {
        this.loading = false;
      }
    },

    loadImpersonationFromStorage() {
      if (typeof window === "undefined") return;

      const storedUid = window.localStorage.getItem(impersonationStorageKey);
      this.impersonated_uid = storedUid || null;
    },

    persistImpersonationToStorage() {
      if (typeof window === "undefined") return;

      if (this.impersonated_uid) {
        window.localStorage.setItem(
          impersonationStorageKey,
          this.impersonated_uid
        );
      } else {
        window.localStorage.removeItem(impersonationStorageKey);
      }
    },

    startImpersonationStorageSync() {
      if (typeof window === "undefined" || this.storageSyncInitialized) return;

      this.storageSyncHandler = async event => {
        if (event.key !== impersonationStorageKey) return;

        const nextUid = event.newValue || null;
        if (nextUid === this.impersonated_uid) return;

        this.impersonated_uid = nextUid;
        if (this.user) {
          await this.refreshCurrentUserData();
        } else {
          this.resetUserScopedState();
        }
      };

      window.addEventListener("storage", this.storageSyncHandler);
      this.storageSyncInitialized = true;
    },

    async impersonateUser(firebaseUid = null) {
      const normalizedUid = firebaseUid?.trim?.() || null;

      if (normalizedUid && !this.isActualAdmin) {
        console.warn("Only admin users can enable impersonation mode.");
        return this.effectiveUid;
      }

      if (!this.user?.uid) {
        this.impersonated_uid = normalizedUid;
        this.persistImpersonationToStorage();
        this.resetUserScopedState();
        return null;
      }

      if (normalizedUid === this.user.uid) {
        this.impersonated_uid = null;
      } else {
        this.impersonated_uid = normalizedUid;
      }

      this.persistImpersonationToStorage();
      await this.refreshCurrentUserData();
      return this.effectiveUid;
    },

    initialize() {
      if (this.initialized && this.authUnsubscribe) return;

      this.loadImpersonationFromStorage();
      this.startImpersonationStorageSync();

      const auth = getAuth();
      this.loading = true;
      this.initialized = true;
      this.authUnsubscribe = onAuthStateChanged(auth, user => {
        if (user) {
          this.user = user;
          this.refreshCurrentUserData().catch(() => {});
        } else {
          this.user = null;
          this.impersonated_uid = null;
          this.persistImpersonationToStorage();
          this.actual_roles = [];
          this.resetUserScopedState();
          this.loading = false;
        }
      });
    },

    async getActualUserRoles(refresh = false) {
      if (!refresh && this.actual_roles.length > 0) return this.actual_roles;
      if (!this.user?.email) {
        this.actual_roles = [];
        return this.actual_roles;
      }

      try {
        const userRolesRef = doc(window.db, "userRoles", this.user.email);
        const userRolesSnap = await getDoc(userRolesRef);
        if (userRolesSnap.exists()) {
          this.actual_roles = userRolesSnap.get("roles") || [];
        } else {
          this.actual_roles = [];
        }
      } catch (error) {
        this.actual_roles = [];
        if (error?.code !== "permission-denied") {
          console.error("Failed to load actual user roles:", error);
        }
      }

      return this.actual_roles;
    },

    async loadPreferredContactSelection() {
      if (!this.effectiveUid) {
        this.selectedContactId = null;
        return null;
      }

      try {
        const preferencesRef = doc(
          window.db,
          memberContactPreferencesCollection,
          this.effectiveUid
        );
        const preferencesSnap = await getDoc(preferencesRef);

        if (!preferencesSnap.exists()) {
          this.selectedContactId = null;
          return null;
        }

        const preferredContactId =
          preferencesSnap.data().preferredPartyDonationContactId || null;
        this.selectedContactId = preferredContactId;
        this.mobileNumber = preferencesSnap.data().mobileNumber || "";
        return preferredContactId;
      } catch (error) {
        this.selectedContactId = null;
        if (error?.code === "permission-denied") {
          console.warn(
            "Preferred party donation contact is not available for this account yet."
          );
          return null;
        }

        console.error(
          "Failed to load preferred party donation contact:",
          error
        );
        return null;
      }
    },

    async saveMobileNumber(number) {
      if (!this.effectiveUid) return;
      try {
        const preferencesRef = doc(
          window.db,
          memberContactPreferencesCollection,
          this.effectiveUid
        );
        await setDoc(preferencesRef, { mobileNumber: number }, { merge: true });
        this.mobileNumber = number;
      } catch (error) {
        console.error("Failed to save mobile number:", error);
      }
    },

    async savePreferredContactSelection(contactId) {
      if (!this.effectiveUid) {
        return null;
      }

      try {
        const preferencesRef = doc(
          window.db,
          memberContactPreferencesCollection,
          this.effectiveUid
        );
        await setDoc(
          preferencesRef,
          {
            preferredPartyDonationContactId: contactId || null,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        this.selectedContactId = contactId || null;
        return this.selectedContactId;
      } catch (error) {
        if (error?.code === "permission-denied") {
          console.warn(
            "Preferred party donation contact could not be saved for this account."
          );
          return null;
        }

        console.error(
          "Failed to save preferred party donation contact:",
          error
        );
        return null;
      }
    },
    /**
     * Gets the contact document data for the currently signed-in user. If refresh
     * is true, it will always fetch the latest version from Firestore. If
     * false, it will return the previously fetched contact if it exists.
     * @param {boolean} [refresh=false] Forces a refresh
     * @returns {Promise<Object>} A promise that resolves with the contact
     *     document, or null if no contact document exists for the user.
     */
    async getContact(refresh = false) {
      if (!refresh && this.contact) return this.contact;
      if (!this.effectiveUid) return;
      this.loadingContact = true;
      try {
        const contactRef = await this.getContactRef(refresh);
        if (contactRef) {
          this.contactId = contactRef.id;
          console.debug(`Contact ID is ${contactRef.id}`);
          this.contact = contactRef.data();
        } else {
          this.contact = null;
          this.contactId = null;
        }
      } finally {
        this.loadingContact = false;
      }
      return this.contact;
    },
    /**
     * Gets a reference to the contacts associated with the logged in user.
     *
     * @param {boolean} [refresh=false] forces a refresh
     * @returns {Promise<String>}
     */
    async getContactRef(refresh = false) {
      if (!refresh && this.contactRef) return this.contactRef;
      if (!this.effectiveUid) return;

      const contactsDbRef = collection(window.db, "contacts");
      this.contactLookupAttempted = true;

      try {
        console.debug(`Requesting contacts for ${this.effectiveUid}`);
        const q = await query(
          contactsDbRef,
          where("firebase_user_id", "==", this.effectiveUid)
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.size === 1) {
          this.availableContacts = [];
          this.contactRef = querySnapshot.docs[0];
          return this.contactRef;
        }

        if (querySnapshot.size === 0) {
          this.contactRef = null;
          this.availableContacts = [];
          if (
            !this.isImpersonating &&
            this.user.emailVerified &&
            this.user.email
          ) {
            const createdContactRef = await this.createContactForVerifiedUser();
            if (createdContactRef) {
              return createdContactRef;
            }
          }

          console.info("No contact record linked to this account yet.");
          return null;
        }

        // Multiple contacts linked to this user — let the user choose.
        this.availableContacts = querySnapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        console.warn(
          `Multiple contacts are linked to this user ID (${querySnapshot.size}). Awaiting contact selection.`
        );

        if (this.selectedContactId) {
          const selectedDoc = querySnapshot.docs.find(
            d => d.id === this.selectedContactId
          );
          if (selectedDoc) {
            this.contactRef = selectedDoc;
            return selectedDoc;
          }

          this.selectedContactId = null;
        }

        this.contactRef = null;
        return null;
      } catch (error) {
        this.contactRef = null;
        if (error?.code === "permission-denied") {
          console.warn("Contact lookup is not permitted for this account yet.");
          return null;
        }

        console.error("Failed to load contact reference:", error);
        return null;
      }
    },

    async createContactForVerifiedUser() {
      if (
        this.isImpersonating ||
        !this.user?.uid ||
        !this.user?.emailVerified ||
        !this.user?.email
      ) {
        return null;
      }

      // const contactsDbRef = collection(window.db, "contacts");
      const fallbackName =
        this.user.displayName || this.user.email.split("@")[0] || "Member";

      try {
        const contactDoc = {
          firebase_user_id: this.user.uid,
          name: fallbackName,
          email: this.user.email,
          phone: "",
          street_1: "",
          street_2: "",
          city: "",
          state: "",
          postal_code: "",
          status: "Active",
          source: "gcdp.org",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        const contactDocRef = doc(window.db, "contacts", this.user.uid);
        await setDoc(contactDocRef, contactDoc, { merge: true });

        const contactSnap = await getDoc(contactDocRef);
        if (!contactSnap.exists()) {
          return null;
        }

        this.contactRef = contactSnap;
        this.contact = contactSnap.data();
        this.contactId = contactSnap.id;
        this.contactLookupAttempted = true;
        return contactSnap;
      } catch (error) {
        if (error?.code === "permission-denied") {
          console.warn(
            "Contact auto-creation is not permitted for this account yet."
          );
          return null;
        }

        console.error("Failed to auto-create contact record:", error);
        return null;
      }
    },

    /**
     * Selects a contact from the list of available contacts (multiple-contact scenario).
     * Fetches the chosen contact's document, updates the store, and reloads
     * memberships and contributions for the newly selected contact.
     *
     * @param {string} contactId  Firestore document ID of the chosen contact.
     */
    async selectContact(contactId) {
      const found = this.availableContacts.find(c => c.id === contactId);
      if (!found) {
        console.warn(
          "selectContact: contactId not found in availableContacts."
        );
        return;
      }

      this.selectedContactId = contactId;
      this.loadingContact = true;

      try {
        const contactDocRef = doc(window.db, "contacts", contactId);
        const contactSnap = await getDoc(contactDocRef);

        if (!contactSnap.exists()) {
          console.warn("selectContact: contact document no longer exists.");
          this.loadingContact = false;
          return;
        }

        this.contactRef = contactSnap;
        this.contactId = contactId;
        this.contact = contactSnap.data();

        // Clear stale data and reload for the selected contact.
        this.memberships = [];
        this.allMemberships = [];
        this.membershipTotalCount = 0;
        this.lastMembership = null;
        this.contributions = [];

        await this.savePreferredContactSelection(contactId);

        await this.getUserMemberships(true);
        await this.getUserContributions(true);
      } finally {
        this.loadingContact = false;
      }
    },
    async updateCurrentUserContact(contactUpdates = {}) {
      if (!this.contactId || !this.effectiveUid) {
        return null;
      }
      this.updateUserContact(this.contactId, contactUpdates);
    },
    async updateUserContact(contactId, contactUpdates = {}) {
      const contactDocRef = doc(window.db, "contacts", contactId);
      const normalizedUpdates = {
        name: contactUpdates.name?.trim?.() || "",
        email: contactUpdates.email?.trim?.() || "",
        phone: contactUpdates.phone?.trim?.() || "",
        street_1: contactUpdates.street_1?.trim?.() || "",
        street_2: contactUpdates.street_2?.trim?.() || "",
        city: contactUpdates.city?.trim?.() || "",
        state: contactUpdates.state?.trim?.() || "",
        postal_code: contactUpdates.postal_code?.trim?.() || "",
      };

      try {
        await updateDoc(contactDocRef, {
          ...normalizedUpdates,
          firebase_user_id: this.effectiveUid,
          updatedAt: serverTimestamp(),
        });

        this.contact = {
          ...(this.contact || {}),
          ...normalizedUpdates,
          firebase_user_id: this.effectiveUid,
        };
        return this.contact;
      } catch (error) {
        if (error?.code === "permission-denied") {
          console.warn("Contact update is not permitted.");
          return null;
        }

        console.error("Failed to update contact record:", error);
        return null;
      }
    },
    /**
     * Gets the active user's roles. If refresh is true, it will always fetch
     * the latest version from Firestore. If false, it will return the previously
     * fetched roles if they exist.
     * @param {boolean} [refresh=false]
     * @returns {Promise<string[]>}
     */
    async getCurrentUserRoles(refresh = false) {
      if (!refresh && this.roles.length > 0) return this.roles;
      if (!this.user) return [];

      if (!this.isImpersonating) {
        const actualRoles = await this.getActualUserRoles(refresh);
        this.roles = [...actualRoles];
        return this.roles;
      }

      try {
        let roleLookupEmail = this.user.email || null;

        if (this.isImpersonating) {
          const contactRef = await this.getContactRef(refresh);
          roleLookupEmail =
            contactRef?.data?.()?.email || contactRef?.get?.("email") || null;
        }

        if (!roleLookupEmail) {
          this.roles = [];
          return this.roles;
        }

        const userRolesRef = doc(window.db, "userRoles", roleLookupEmail);
        const userRolesSnap = await getDoc(userRolesRef);
        if (userRolesSnap.exists()) {
          this.roles = userRolesSnap.get("roles") || [];
        } else {
          this.roles = [];
          console.info("No roles document found for this account yet.");
        }
      } catch (error) {
        this.roles = [];
        if (error?.code === "permission-denied") {
          console.warn("Role lookup is not permitted for this account yet.");
          return this.roles;
        }

        console.error("Failed to load user roles:", error);
      }

      return this.roles;
    },

    /**
     * Updates the lastMembership property to the membership with the latest endDate.
     * Iterates through the user's memberships to find the one with the most recent
     * end date and assigns it to lastMembership. If the memberships array is empty,
     * lastMembership will be set to null.
     */
    async updateLastMembership() {
      let lastMembership = null;
      this.memberships.forEach(membership => {
        if (
          lastMembership === null ||
          membership.endDate === null ||
          (lastMembership.endDate &&
            membership.endDate > lastMembership.endDate)
        ) {
          lastMembership = membership;
        }
      });
      this.lastMembership = lastMembership;
    },
    /**
     * Gets the active user's memberships. If refresh is true, it will always fetch
     * the latest version from Firestore. If false, it will return the previously
     * fetched memberships if they exist.
     * @param {boolean} [refresh=false] Whether to refresh the memberships
     * @return {Promise<Array<Object>>} A promise that resolves with an array of
     *     membership objects, which have the following properties:
     *     {
     *         organization: string,
     *         title: string,
     *         startDate: Date,
     *         endDate: Date,
     *     }
     */
    async getUserMemberships(refresh = false) {
      if (!refresh && this.allMemberships.length > 0) {
        return this.memberships;
      }
      if (!this.contactId) return [];

      this.loadingMemberships = true;

      const contactRef = await this.getContactRef();
      if (!contactRef) {
        this.memberships = [];
        this.allMemberships = [];
        this.membershipTotalCount = 0;
        this.lastMembership = null;
        this.loadingMemberships = false;
        return [];
      }

      try {
        const membershipsRef = collection(window.db, "memberships");
        const q = query(
          membershipsRef,
          where("contact_id", "==", contactRef.ref)
        );
        const querySnapshot = await getDocs(q);
        const memberships = [];
        querySnapshot.forEach(doc => {
          memberships.push(doc.data());
        });

        console.debug("Memberships:", memberships);
        // Store all memberships sorted
        this.allMemberships = Object.values(memberships).sort((a, b) => {
          const toMillis = dateValue => {
            if (!dateValue) return null;
            if (dateValue instanceof Date) return dateValue.getTime();
            if (typeof dateValue.toMillis === "function")
              return dateValue.toMillis();
            if (typeof dateValue.seconds === "number") {
              return dateValue.seconds * 1000;
            }

            const parsed = new Date(dateValue).getTime();
            return Number.isNaN(parsed) ? null : parsed;
          };

          const aEndMillis = toMillis(a.endDate);
          const bEndMillis = toMillis(b.endDate);

          if (aEndMillis === null && bEndMillis === null) return 0;
          if (aEndMillis === null) return -1;
          if (bEndMillis === null) return 1;

          return bEndMillis - aEndMillis;
        });
        this.membershipTotalCount = this.allMemberships.length;
        this.updateDisplayedMemberships();
        this.updateLastMembership();
        return this.memberships;
      } catch (error) {
        this.memberships = [];
        this.allMemberships = [];
        this.membershipTotalCount = 0;
        this.lastMembership = null;
        if (error?.code === "permission-denied") {
          console.warn(
            "Membership history is not available for this account yet."
          );
          return [];
        }

        console.error("Failed to load membership history:", error);
        return [];
      } finally {
        this.loadingMemberships = false;
      }
    },
    updateDisplayedMemberships() {
      this.memberships = this.allMemberships.slice(0, this.membershipPageSize);
    },
    loadMoreMemberships() {
      if (this.loadingMoreMemberships) return;
      if (this.memberships.length >= this.allMemberships.length) return;

      this.loadingMoreMemberships = true;
      try {
        const currentCount = this.memberships.length;
        const newCount = Math.min(
          currentCount + this.membershipPageSize,
          this.allMemberships.length
        );
        this.memberships = this.allMemberships.slice(0, newCount);
      } finally {
        this.loadingMoreMemberships = false;
      }
    },
    /**
     * Gets the collections associated with this contact.
     *
     * @param {boolean} refresh instructs the function to refresh the data.
     * @returns {Promise<Array<Object>>} contributions
     */
    async getUserContributions(refresh = false) {
      if (!refresh && this.contributions.length > 0) return this.contributions;
      if (!this.contactId) return [];
      this.loadingContributions = true;
      const contactRef = await this.getContactRef();
      if (!contactRef) {
        this.contributions = [];
        this.loadingContributions = false;
        return [];
      }

      try {
        // Prefer the pre-aggregated contact_contributions document (one read).
        const aggregateDocRef = doc(
          window.db,
          "contact_contributions",
          this.contactId
        );
        const aggregateSnap = await getDoc(aggregateDocRef);

        if (aggregateSnap.exists()) {
          const data = aggregateSnap.data();
          const nested = Array.isArray(data.contributions)
            ? data.contributions
            : [];
          this.contributions = nested.map((c, i) => ({
            id: c.id ?? String(i),
            ...c,
          }));
          console.debug(
            "Contributions loaded from aggregate:",
            this.contributions.length
          );
          return this.contributions;
        }

        // Fall back to querying the contributions collection directly.
        console.debug(
          "No contact_contributions aggregate found; falling back to collection query."
        );
        const contributionsRef = collection(window.db, "contributions");
        const q = query(
          contributionsRef,
          where("contact_id", "==", contactRef.ref)
        );
        const contributionsSnap = await getDocs(q);

        this.contributions = contributionsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        return this.contributions;
      } catch (error) {
        this.contributions = [];
        if (error?.code === "permission-denied") {
          console.warn(
            "Contribution history is not available for this account yet."
          );
          return [];
        }

        console.error("Failed to load contribution history:", error);
        return [];
      } finally {
        this.loadingContributions = false;
      }
    },
  },
  persist: true,
});
